/**
 * ISBN API Service
 * 
 * ISBN Lookup und Band hinzufügen
 * 
 * Endpoints:
 * GET  /lookup/isbn/{isbn} - ISBN Lookup (externe Daten)
 * POST /library/isbn       - Band per ISBN zur Library hinzufügen
 */

import { get, post } from '../client';

/**
 * ISBN Lookup - Externe Daten abrufen
 * @param {string} isbn - ISBN-10 oder ISBN-13
 * @returns {Promise<{success: boolean, data?: IsbnLookupResult, error?: object}>}
 * 
 * Response Data:
 * {
 *   isbn: string,
 *   band_title: string,
 *   manga_title: string,
 *   band_number: number,
 *   author: string,
 *   publisher: string,
 *   published_date: string,
 *   language: string,
 *   cover_media_key: string
 * }
 * 
 * Errors: 400 INVALID_ISBN, 404 NOT_FOUND
 */
export const lookup = async (isbn) => {
  // ISBN normalisieren (Bindestriche entfernen)
  const normalizedIsbn = isbn.replace(/-/g, '').trim();
  return get(`/lookup/isbn/${normalizedIsbn}`);
};

/**
 * Band per ISBN zur Library hinzufügen
 * @param {string} isbn - ISBN-10 oder ISBN-13
 * @returns {Promise<{success: boolean, data?: AddByIsbnResult, error?: object}>}
 * 
 * Response Data:
 * {
 *   manga_id: number,
 *   band_id: number,
 *   band_title: string,
 *   band_number: number,
 *   reading_status: string (UNREAD)
 * }
 * 
 * Errors: 400 INVALID_ISBN, 404 NOT_FOUND, 409 ALREADY_OWNED
 */
export const addToLibrary = async (isbn) => {
  // ISBN normalisieren (Bindestriche entfernen)
  const normalizedIsbn = isbn.replace(/-/g, '').trim();
  return post('/library/isbn', { isbn: normalizedIsbn });
};

/**
 * Validiert ISBN-Format (Client-seitig)
 * @param {string} isbn - ISBN String
 * @returns {boolean} - true wenn Format gültig
 */
export const isValidIsbnFormat = (isbn) => {
  const cleaned = isbn.replace(/-/g, '').trim();
  
  // ISBN-10: 10 Ziffern (letzte kann X sein)
  if (/^[0-9]{9}[0-9X]$/i.test(cleaned)) {
    return true;
  }
  
  // ISBN-13: 13 Ziffern, beginnt mit 978 oder 979
  if (/^97[89][0-9]{10}$/.test(cleaned)) {
    return true;
  }
  
  return false;
};

/**
 * Normalisiert ISBN (entfernt Bindestriche und Leerzeichen)
 * @param {string} isbn - ISBN String
 * @returns {string} - Normalisierte ISBN
 */
export const normalizeIsbn = (isbn) => {
  return isbn.replace(/[-\s]/g, '').trim().toUpperCase();
};

// ============================================================================
// TYPES (JSDoc)
// ============================================================================

/**
 * @typedef {object} IsbnLookupResult
 * @property {string} isbn
 * @property {string} band_title
 * @property {string} manga_title
 * @property {number} band_number
 * @property {string} author
 * @property {string} publisher
 * @property {string} published_date
 * @property {string} language
 * @property {string} cover_media_key
 */

/**
 * @typedef {object} AddByIsbnResult
 * @property {number} manga_id
 * @property {number} band_id
 * @property {string} band_title
 * @property {number} band_number
 * @property {string} reading_status
 */
